/* eslint-disable @nx/enforce-module-boundaries */
import { useDisclosure, useToast } from '@mybridge/ui/hooks';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  applyJob,
  getActiveJobs,
  getApplyedJobs,
  getJobsDetail,
  getPublicJobsDetail,
  getSavedJobs,
  getSearchJobs,
  getUserJobPreferences,
  removeJob,
  removeUserJobPreferencesResume,
  reportJob,
  saveJobs,
  setUserJobPreferences,
  setUserJobPreferencesJSON,
} from 'v4/store/actions/Job.Action';
import { getNextPageFromURL } from 'v4/utils/utils';
import _ from 'lodash';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
export const JobsListingContext = createContext({});

export const useJobsListingContext = ({
  refetchSuggestedJobsListingEnabled = false,
  refetchSavedJobsListingEnabled = false,
  refetchAppliedJobsListingEnabled = false,
  jobPreferencesRefetchEnabled = false,
  refetchSearchJobsListingEnabled = false,
  slug,
}) => {
  const [appliedJob, setAppliedJob] = useState();
  const [jobToReport, setJobToReport] = useState();
  const [filters, setFilters] = useState();
  const applyDisc = useDisclosure();
  const reportDisc = useDisclosure();
  const [errorExistCompany,setErrorExistCompany]=useState(true);
  const { userProfileInfo } = useSelector((state) => state?.userProfile) ?? {};
  const { loggedIn } = useSelector((state) => state?.user) ?? {};
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const toast = useToast({
    position: 'top',
  });

  // job details
  const {
    data: jobDetails,
    isFetching: jobDetailsFetching,
    refetch: refetchJobDetails,
  } = useQuery({
    queryKey: ['job-details', slug],
    queryFn: async () => {
      const resp = slug?.length
        ? loggedIn
          ? (await dispatch(getJobsDetail(slug)))?.payload
          : (await dispatch(getPublicJobsDetail(slug)))?.payload
        : null;
      return resp;
    },
    refetchOnWindowFocus: false,
  });

  // applied job details
  const {
    data: appliedJobDetails,
    isFetching: appliedJobDetailsFetching,
    refetch: refetchAppliedJobDetails,
  } = useQuery({
    queryKey: ['applied-job-details', appliedJob?.id],
    queryFn: async () => {
      const resp = appliedJob?.id
        ? (await dispatch(getJobsDetail(appliedJob?.id)))?.payload
        : null;
      return resp;
    },
    refetchOnWindowFocus: false,
  });

  // search listings
  const {
    data: searchJobsListingPages = { pages: [] },
    isFetching: searchJobsListingIsFetching,
    isLoading: searchJobsListingIsLoading,
    refetch: refetchSearchJobsListing,
    fetchNextPage: fetchSearchJobsListingNextPage,
    hasNextPage: searchJobsListingHasNextPage,
  } = useInfiniteQuery({
    queryKey: ['search-jobs-listing', filters],
    queryFn: async ({ queryKey, pageParam = 1 }) => {
      const resp =
        (await dispatch(getSearchJobs({ query: queryKey?.[1], pageParam })))
          ?.payload ?? {};
      return resp;
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage?.next ? getNextPageFromURL(lastPage?.next) : undefined;
    },
    refetchOnWindowFocus: false,
    enabled: refetchSearchJobsListingEnabled,
  });

  // suggested listings
  const {
    data: suggestedJobsListingPages = { pages: [] },
    isFetching: suggestedJobsListingIsFetching,
    refetch: refetchSuggestedJobsListing,
    fetchNextPage: fetchSuggestedJobsListingNextPage,
    hasNextPage: suggestedJobsListingHasNextPage,
  } = useInfiniteQuery({
    queryKey: ['suggested-jobs-listing'],
    queryFn: async ({ pageParam, queryKey }) => {
      return (await dispatch(getActiveJobs({ pageParam })))?.payload ?? {};
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage?.next ? getNextPageFromURL(lastPage?.next) : undefined;
    },
    enabled: refetchSuggestedJobsListingEnabled,
  });

  // my listings
  const {
    data: savedJobsListingPages = { pages: [] },
    isFetching: savedJobsListingIsFetching,
    refetch: refetchSavedJobsListing,
    fetchNextPage: fetchSavedJobsListingNextPage,
  } = useInfiniteQuery({
    queryKey: ['my-jobs-listing'],
    queryFn: async () => {
      // return (await dispatch(getSavedJobs()))?.payload ?? {};
      if (!loggedIn) return {};
      return (await dispatch(getSavedJobs()))?.payload ?? {};
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage?.next ? getNextPageFromURL(lastPage?.next) : undefined;
    },
    // enabled: refetchSavedJobsListingEnabled,
    enabled: !!loggedIn && refetchSavedJobsListingEnabled,
  });

  // applied listings
  const {
    data: appliedJobsListingPages = { pages: [] },
    isFetching: appliedJobsListingIsFetching,
    isLoading: appliedJobsListingIsLoading,
    refetch: refetchAppliedJobsListing,
    fetchNextPage: fetchAppliedJobsListingNextPage,
    hasNextPage: appliedJobsListingHasNextPage,
  } = useInfiniteQuery({
    queryKey: ['applied-jobs-listing'],
    queryFn: async () => {
      // return (await dispatch(getApplyedJobs()))?.payload ?? {};
      if (!loggedIn) return {};
      return (await dispatch(getApplyedJobs()))?.payload ?? {};
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage?.next ? getNextPageFromURL(lastPage?.next) : undefined;
    },
    // enabled: refetchAppliedJobsListingEnabled,
    enabled: !!loggedIn && refetchAppliedJobsListingEnabled,
  });

  /**
   * My job preferences
   */
  const {
    data: jobPreferences,
    refetch: refetchJobPreferences,
    isFetching: jobPreferencesFetching,
    isLoading: jobPreferencesLoading,
  } = useQuery({
    queryKey: ['jobPreferences'],
    queryFn: async () => {
      // if (!loggedIn) {
      //   return;
      // } else {
        // const resp = await dispatch(getUserJobPreferences());
        // return resp?.payload;
        if (!loggedIn) return;
        const resp = await dispatch(getUserJobPreferences());
        return resp?.payload;
      // }
    },
    refetchOnWindowFocus: false,
    // enabled: jobPreferencesRefetchEnabled,
    enabled: !!loggedIn && jobPreferencesRefetchEnabled,
  });

  /**
   * My job preferences for resumes
   */
  const {
    data: jobPreferencesResume,
    refetch: refetchJobPreferencesResume,
  } = useQuery({
    queryKey: ['jobPreferencesResume'],
    queryFn: async () => {
        // const resp = await dispatch(getUserJobPreferences());
        // return resp?.payload;
        if (!loggedIn) return;
        const resp = await dispatch(getUserJobPreferences());
        return resp?.payload;
    },
    refetchOnWindowFocus: false,
    // enabled: jobPreferencesRefetchEnabled,
    enabled: !!loggedIn && jobPreferencesRefetchEnabled,
  });

  /**
   * Update job preferences
   */
  const {
    data: updateJobPreferencesResult,
    isLoading: updateJobPreferencesLoading,
    mutateAsync: updateJobPreferencesAsync,
  } = useMutation({
    mutationKey: ['job-pref-update'],
    mutationFn: async ({ payload, mode }) => {
      if (mode === 'form') {
        return (await dispatch(setUserJobPreferences(payload)))?.payload;
      } else {
        return (await dispatch(setUserJobPreferencesJSON(payload)))?.payload;
      }
    },
  });

  /**
   * save a job
   */
  const {
    data: saveJobResult,
    isLoading: saveJobIsLoading,
    mutateAsync: saveJobAsync,
  } = useMutation({
    mutationKey: ['bookmark-job'],
    mutationFn: async (payload) => {
      return (await dispatch(saveJobs(payload)))?.payload;
    },
  });

  /**
   * report a job
   */
  const {
    data: reportJobResult,
    isLoading: reportJobIsLoading,
    mutateAsync: reportJobAsync,
  } = useMutation({
    mutationKey: ['report-job'],
    mutationFn: async (payload) => {
      return (await dispatch(reportJob(payload)))?.payload;
    },
  });

  /**
   * apply job
   */
  const {
    data: applyJobResult,
    isLoading: applyJobIsLoading,
    mutateAsync: applyJobAsync,
  } = useMutation({
    mutationKey: ['apply-job'],
    mutationFn: async (payload) => {
      return (await dispatch(applyJob(payload)))?.payload;
    },
  });

  /**
   * remove saved/bookmarked job
   */
  const {
    data: removeSavedJobResult,
    isLoading: removeSavedJobIsLoading,
    mutateAsync: removeSavedJobAsync,
  } = useMutation({
    mutationKey: ['remove-bookmarked-job'],
    mutationFn: async (payload) => {
      return (await dispatch(removeJob(payload)))?.payload;
    },
  });

  /**
   * Remove resume
   */
  const {
    data: removeJobPreferencesResumeResult,
    isLoading: removeJobPreferencesResumeLoading,
    mutateAsync: removeJobPreferencesResumeAsync,
  } = useMutation({
    mutationKey: ['job-pref-update-remove-resume'],
    mutationFn: async (payload) => {
      return (await dispatch(removeUserJobPreferencesResume(payload)))?.payload;
    },
  });

  /** util methods */

  /**
   * update job preferences
   * @param {*} data
   */
  const updateJobPreferences = async (payload, mode = 'form', showToast = true, refetch=true, resume=false) => {
    try {
      const resp = await updateJobPreferencesAsync({ payload, mode });
      if (resp?.state_code >= 400) {
        toast({ title: t('toast.invalidDetails') || 'Invalid details provided!', status: 'error' });
        return false;
      } else {
        if(refetch) refetchJobPreferences();
        if(resume) refetchJobPreferencesResume();

        if (showToast) {
          toast({
            title: t('jobs.updatedSuccessfully') || 'updated successfully!',
            status: 'success',
            position: 'top',
            isClosable: true,
            duration: 3000,
          });
        }
        return true;
      }
    } catch (e) {
      toast({
        title: t('jobs.errorUpdatingPreferences') || 'Error while updating profile preferences!',
        status: 'error',
      });
    }
  };

  /**
   * Save job
   * @param {*} data
   */
  const applyJob_ = async (payload) => {
    try {
      const resp = await applyJobAsync(payload);
      
      if(resp?.id){
        refetchAppliedJobsListing();
        refetchSavedJobsListing();
        refetchSuggestedJobsListing();
        toast({
          title: t('toast.jobApplySuccess') || 'Job application submitted successfully!',
          status: 'success',
        });
        applyDisc?.onClose?.();
      }else{
        toast({ title: t('toast.jobApplyError') || 'Unable to apply for the job!', status: 'error' });
      }
    } catch (e) {
      toast({
        title: t('toast.jobApplyErrorGeneric') || 'Error while applying for job!',
        status: 'error',
      });
    }
  };

  /**
   * Save job
   * @param {*} data
   */
  const saveJob = async ({ job_id }) => {
    try {
      const resp = await saveJobAsync({
        user_id: userProfileInfo?.id,
        job_id,
      });
      if (resp?.state_code >= 400) {
        toast({ title: 'Unable to save job!', status: 'error' });
      } else if(Object.hasOwn(resp, "invalid") || Object.hasOwn(resp, "null") || Object.hasOwn(resp, "required")){
        toast({
          title: t('toast.jobSaveError') || 'Error while saving job!',
          status: 'error',
        });
      }else{
        refetchSavedJobsListing();
        refetchSuggestedJobsListing();
        toast({ title: t('jobs.jobSaved') || 'Job saved successfully!', status: 'success' });
      }
    } catch (e) {
      toast({
        title: t('toast.jobSaveError') || 'Error while saving job!',
        status: 'error',
      });
    }
  };

  /**
   * Remove saved job
   * @param {*} data
   */
  const removeSavedJob = async ({ id, job_id }) => {
    try {
      let savedJobRecordId = id;
      if (!savedJobRecordId && job_id) {
        const savedJob = savedJobsListing?.find?.(sj => sj?.job_details?.id === job_id);
        savedJobRecordId = savedJob?.id;
      }
      const resp = await removeSavedJobAsync({
        id: savedJobRecordId,
      });
      if (resp?.state_code >= 400) {
        toast({
          title: t('jobs.unableToRemoveBookmark') || 'Unable to remove saved/bookmarked job!',
          status: 'error',
        });
      } else {
        refetchSavedJobsListing();
        refetchSuggestedJobsListing();
        toast({
          title: t('jobs.bookmarkRemoved') || 'Job bookmark removed successfully!',
          status: 'success',
        });
      }
    } catch (e) {
      toast({
        title: t('toast.jobRemoveError') || 'Error while removing saved/bookmarked job!',
        status: 'error',
      });
    }
  };

  /**
   * Report a job
   * @param {*} data
   */
  const reportJob_ = async (payload) => {
    try {
      const resp = await reportJobAsync(payload);
      if (resp?.state_code >= 400) {
        toast({
          title: t('toast.jobReportError') || 'Unable to report job!',
          status: 'error',
        });
      } else {
        // toast({
        //   title: 'Job reported successfully!',
        //   status: 'success',
        // });
        setErrorExistCompany(false)
        // reportDisc?.onClose?.();
      }
    } catch (e) {
      toast({
        title: t('toast.jobReportErrorGeneric') || 'Error while reporting job!',
        status: 'error',
      });
    }
  };

  /**
   * remove resume
   * @param {*} data
   */
  const removeResume_ = async (resume, refetch=true, resumeFetch=false) => {
    try {
      const resp = await removeJobPreferencesResumeAsync(resume);
      if (resp?.state_code >= 400) {
        toast({ title: t('toast.invalidDetails') || 'Invalid details provided!', status: 'error' });
      } else {
        if(refetch) refetchJobPreferences();
        if(resumeFetch) refetchJobPreferencesResume();
        toast({
          title: t('toast.resumeRemoveSuccess') || 'Resume removed successfully!',
          status: 'success',
        });
      }
    } catch (e) {
      toast({
        title: t('toast.resumeRemoveError') || 'Error while removing resume!',
        description: e?.message,
        status: 'error',
      });
    }
  };

  useEffect(() => {
    refetchAppliedJobDetails?.();
  }, []);

  const suggestedJobsListing = useMemo(
    () =>
      [].concat.apply(
        [],
        suggestedJobsListingPages?.pages?.map((p) => p?.results ?? [])
      ),
    [suggestedJobsListingPages]
  );

  const appliedJobsListing = useMemo(
    () =>
      [].concat.apply(
        [],
        appliedJobsListingPages?.pages?.map((p) => p?.results ?? [])
      ),
    [appliedJobsListingPages]
  );

  const savedJobsListing = useMemo(
    () =>
      [].concat.apply(
        [],
        savedJobsListingPages?.pages?.map((p) => p?.results ?? [])
      ),
    [savedJobsListingPages]
  );

  const isSavedJob = (id) => {
    return savedJobsListing?.find?.((sj) => sj?.job_details?.id === id);
  };
  const isAppliedJob = (id) => {
    return appliedJobsListing?.find?.((sj) => sj?.job_details?.id === id);
  };
  // console.log('suggestedJobsListingPages', appliedJobsListingPages);
  return {
    suggestedJobsListing,
    suggestedJobsListingIsFetching,
    refetchSuggestedJobsListing,
    fetchSuggestedJobsListingNextPage,
    suggestedJobsListingHasNextPage,
    suggestedJobsListingCount: suggestedJobsListingPages?.pages?.length * 10,
    // saved
    savedJobsListing,
    savedJobsListingIsFetching,
    refetchSavedJobsListing,
    fetchSavedJobsListingNextPage,

    // applied
    appliedJobsListing,
    appliedJobsListingIsFetching,
    appliedJobsListingIsLoading,
    refetchAppliedJobsListing,
    fetchAppliedJobsListingNextPage,
    appliedJobsListingHasNextPage,
    // prefs
    jobPreferences,
    refetchJobPreferences,
    jobPreferencesFetching,
    jobPreferencesLoading,
    updateJobPreferences,
    updateJobPreferencesLoading,
    
    // prefs resume
    jobPreferencesResume,
    refetchJobPreferencesResume,
    
    removeJobPreferencesResumeResult,
    removeJobPreferencesResumeLoading,
    removeResume: removeResume_,
    // job details
    jobDetails,
    jobDetailsFetching,
    refetchJobDetails,
    // save job
    saveJobResult,
    saveJobIsLoading,
    saveJob,
    isSavedJob,
    // remove saved job
    removeSavedJobResult,
    removeSavedJobIsLoading,
    removeSavedJob,

    // apply
    applyDisc,
    appliedJob,
    setAppliedJob,
    appliedJobDetails,
    appliedJobDetailsFetching,
    refetchAppliedJobDetails,
    isAppliedJob,
    applyJobResult,
    applyJobIsLoading,
    applyJob: applyJob_,

    // report
    reportDisc,
    reportJob: reportJob_,
    reportJobIsLoading,
    reportJobResult,
    jobToReport,
    setJobToReport,
    errorExistCompany,

    //search
    searchJobsListingPages,
    searchJobsListingIsFetching,
    refetchSearchJobsListing,
    fetchSearchJobsListingNextPage,
    searchJobsListingHasNextPage,
    filters,
    setFilters,
    searchJobsListingIsLoading,
  };
};

export const JobsListingProvider = ({ children, props }) => {
  const globalCtx = useJobsListingContext({ ...props });
  return (
    <JobsListingContext.Provider value={globalCtx}>
      {children}
    </JobsListingContext.Provider>
  );
};
